/**
 * DashboardSwitcher — page-top control listing personal + shared dashboards
 * for the current context (GLOBAL or ACCOUNT), the default marked, "New
 * dashboard" (blank or duplicate-current). Same custom-button + .pop-in menu
 * idiom as AccountSelect (this app's established dropdown pattern), not a
 * new one. Each row carries the house kebab (WidgetOverflowMenu idiom) with
 * per-row-gated lifecycle actions — Rename / Duplicate / Publish a shared
 * copy / Set as default / Delete — whose modals live at the switcher root so
 * closing the dropdown never unmounts them mid-flow.
 */
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { FormEvent, CSSProperties } from 'react';
import { toast } from 'sonner';
import { useAuthStore } from '@/store/authStore';
import {
  useCreateDashboard,
  useDuplicateDashboard,
  useUpdateDashboard,
  useDeleteDashboard,
  useSetDefaultDashboard,
  useDashboard,
} from '@/api/dashboardConfig';
import { MUTATION_ERROR_TOAST_ID } from '@/lib/toastIds';
import { FormModal } from '@/components/FormModal';
import { GhostIconButton } from '@/components/GhostIconButton';
import { labelStyle, fieldStyle, btnPrimaryStyle, btnGhostStyle, btnDangerStyle, errorTextStyle } from '@/components/ProjectForm';
import type { DashboardResponse, DashboardContext } from '@/types/domain';

/** The five per-row lifecycle actions, gated per row by ownership / DASHBOARD_MANAGE. */
type LifecycleAction = 'rename' | 'duplicate' | 'publish' | 'setDefault' | 'delete';

function StarIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      style={{ color: 'var(--text-subtle)', flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function KebabIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="5" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="12" cy="19" r="1.8" />
    </svg>
  );
}

/** GhostIconButton doesn't forward a ref, so recover its rendered <button> to
 * restore focus after the row menu closes — the WidgetOverflowMenu idiom. */
function focusTrigger(wrapper: HTMLDivElement | null) {
  wrapper?.querySelector<HTMLButtonElement>('button')?.focus();
}

const rowMenuItemStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', width: '100%', padding: '8px 10px',
  borderRadius: 'var(--radius-sm)', background: 'transparent', border: 'none', cursor: 'pointer',
  textAlign: 'left', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 500,
  color: 'var(--text)',
};

/**
 * DashboardRowMenu — per-row kebab (WidgetOverflowMenu idiom: GhostIconButton
 * trigger + .pop-in role="menu", outside-click + Escape + focus-restore, 44px
 * hit area). Actions are gated by the SAME rule the backend DashboardAccessGuard
 * enforces: PERSONAL rows write on ownership, SHARED rows write on
 * DASHBOARD_MANAGE (no owner fallback). Each action bubbles up to the switcher
 * root via onAction; the root closes the dropdown and opens the modal / fires
 * the hook so the modal survives the dropdown unmounting.
 */
function DashboardRowMenu({ dashboard, memberId, canManageShared, onAction }: {
  dashboard: DashboardResponse;
  memberId: string | null;
  canManageShared: boolean;
  onAction: (action: LifecycleAction) => void;
}) {
  const [open, setOpen] = useState(false);
  // Viewport anchor for the portaled menu (top + distance-from-right), computed
  // from the kebab's rect at open time — the AppShell Projects-menu idiom.
  const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      // The menu is PORTALED to document.body (the switcher dropdown scrolls —
      // overflowY:auto would clip an in-place absolute menu invisible, caught
      // live in the M4 gate), so it is no longer a DOM child of the wrapper:
      // outside-click must check both refs or selecting a menu item closes
      // the menu on mousedown before its click can fire.
      if (wrapperRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        // Capture-phase + stopPropagation so Escape collapses only THIS row's
        // menu (restoring focus to its kebab) without the switcher dropdown's
        // own document Escape listener — registered earlier, so it would run
        // first and yank focus to the switcher trigger while this button
        // unmounts — also firing.
        e.stopPropagation();
        setOpen(false);
        focusTrigger(wrapperRef.current);
      }
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  const isOwner = dashboard.ownerMemberId === memberId;
  // Mirrors DashboardAccessGuard.checkWrite: PERSONAL → ownership only; SHARED →
  // DASHBOARD_MANAGE only (a SHARED row's owner without the permission still can't).
  const canWrite = dashboard.visibility === 'PERSONAL' ? isOwner : canManageShared;
  const canSetDefault = canManageShared && dashboard.visibility === 'SHARED';

  function fire(action: LifecycleAction) {
    setOpen(false);
    onAction(action);
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative', flexShrink: 0 }}>
      <GhostIconButton
        tooltip="Dashboard options"
        label={`${dashboard.name} options`}
        onClick={() => {
          const r = wrapperRef.current?.getBoundingClientRect();
          if (r) setAnchor({ top: r.bottom + 4, right: window.innerWidth - r.right });
          setOpen((v) => !v);
        }}
        ariaHasPopup="menu"
        ariaExpanded={open}
      >
        <KebabIcon />
      </GhostIconButton>
      {open && anchor && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label={`${dashboard.name} options`}
          data-switcher-submenu=""
          className="pop-in"
          style={{
            position: 'fixed', top: anchor.top, right: anchor.right, minWidth: 190,
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-panel)',
            boxShadow: 'var(--shadow-2)', zIndex: 40, padding: 4,
            ['--pop-origin' as unknown as string]: 'top right',
          }}
        >
          {canWrite && (
            <button type="button" role="menuitem" onClick={() => fire('rename')} className="focus-ring" style={rowMenuItemStyle}>
              Rename
            </button>
          )}
          <button type="button" role="menuitem" onClick={() => fire('duplicate')} className="focus-ring" style={rowMenuItemStyle}>
            Duplicate
          </button>
          {canManageShared && (
            <button type="button" role="menuitem" onClick={() => fire('publish')} className="focus-ring" style={rowMenuItemStyle}>
              Publish a shared copy…
            </button>
          )}
          {canSetDefault && (
            <button type="button" role="menuitem" onClick={() => fire('setDefault')} className="focus-ring" style={rowMenuItemStyle}>
              Set as default
            </button>
          )}
          {canWrite && (
            <>
              <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
              <button
                type="button" role="menuitem"
                onClick={() => fire('delete')}
                className="focus-ring" style={{ ...rowMenuItemStyle, color: 'var(--sem-red)' }}
              >
                Delete
              </button>
            </>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

function SwitcherItem({ dashboard, active, memberId, canManageShared, onSelect, onLifecycleAction }: {
  dashboard: DashboardResponse;
  active: boolean;
  memberId: string | null;
  canManageShared: boolean;
  onSelect: () => void;
  onLifecycleAction: (action: LifecycleAction, dashboard: DashboardResponse) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <button
        type="button"
        role="menuitemradio"
        aria-checked={active}
        onClick={onSelect}
        className="focus-ring"
        style={{
          display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, padding: '8px 10px',
          borderRadius: 'var(--radius-sm)', background: active ? 'var(--brand-soft)' : 'transparent',
          border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-sm)', fontWeight: active ? 600 : 400,
          color: active ? 'var(--brand)' : 'var(--text)', transition: 'background 0.1s, color 0.1s',
        }}
        onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLButtonElement).style.background = 'var(--surface-inset)'; }}
        onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
      >
        <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{dashboard.name}</span>
        {dashboard.isDefault && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <span aria-label="Default" title="Default" style={{ display: 'inline-flex', color: 'var(--sem-amber-text)' }}>
              <StarIcon />
            </span>
            <span aria-hidden="true" style={{ fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 600, color: 'var(--sem-amber-text)' }}>
              Default
            </span>
          </span>
        )}
      </button>
      <DashboardRowMenu
        dashboard={dashboard}
        memberId={memberId}
        canManageShared={canManageShared}
        onAction={(action) => onLifecycleAction(action, dashboard)}
      />
    </div>
  );
}

export interface DashboardSwitcherProps {
  dashboards: DashboardResponse[];
  activeDashboardId: string | null;
  context: DashboardContext;
  accountId: string | null;
  onSelect: (id: string) => void;
  /** Fired after the ACTIVE dashboard is deleted so the page can clear its
   * selection and fall back to the default — otherwise the page is stranded
   * on a dead ?dashboardId with an error card (caught live in the M4 gate). */
  onActiveDeleted?: () => void;
}

export function DashboardSwitcher({ dashboards, activeDashboardId, context, accountId, onSelect, onActiveDeleted }: DashboardSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [lifecycleModal, setLifecycleModal] = useState<{ kind: 'rename' | 'delete' | 'publish'; dashboard: DashboardResponse } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const memberId = useAuthStore((s) => s.memberId);
  const canManageShared = useAuthStore((s) => s.hasPermission('DASHBOARD_MANAGE'));
  const duplicate = useDuplicateDashboard();
  const setDefault = useSetDefaultDashboard();

  const inScope = dashboards.filter((d) => d.context === context);
  const personal = inScope.filter((d) => d.visibility === 'PERSONAL' && d.ownerMemberId === memberId);
  const shared = inScope.filter((d) => d.visibility === 'SHARED');
  const active = dashboards.find((d) => d.id === activeDashboardId);
  const label = active ? active.name : 'Dashboard';

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      // Row kebab menus are PORTALED to document.body (the dropdown scrolls;
      // overflow would clip them), so contains() alone would misread a click
      // inside a row menu as outside and close the dropdown out from under it.
      if ((t as Element).closest?.('[data-switcher-submenu]')) return;
      if (wrapperRef.current && !wrapperRef.current.contains(t)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        // Return keyboard focus to the trigger rather than dropping it to <body>.
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function choose(id: string) {
    onSelect(id);
    setOpen(false);
    // Selecting an item unmounts the focused menuitem — restore focus to the
    // trigger so keyboard users aren't dropped to <body>.
    triggerRef.current?.focus();
  }

  // Every kebab action first closes the switcher dropdown and restores focus to
  // the trigger, then either opens a root-level modal (rename/delete/publish) or
  // fires the hook directly (set-as-default / plain duplicate). Because the modal
  // is a sibling of the dropdown — not nested in its {open && …} subtree —
  // closing the dropdown here never unmounts the modal it just opened.
  function handleLifecycleAction(action: LifecycleAction, dashboard: DashboardResponse) {
    setOpen(false);
    triggerRef.current?.focus();
    switch (action) {
      case 'rename':
        setLifecycleModal({ kind: 'rename', dashboard });
        break;
      case 'delete':
        setLifecycleModal({ kind: 'delete', dashboard });
        break;
      case 'publish':
        setLifecycleModal({ kind: 'publish', dashboard });
        break;
      case 'setDefault':
        setDefault.mutate(dashboard.id, {
          onSuccess: () => toast.success(`"${dashboard.name}" is now the default.`),
          onError: () => toast.error("Couldn't set the default dashboard.", { id: MUTATION_ERROR_TOAST_ID }),
        });
        break;
      case 'duplicate':
        duplicate.mutate({ id: dashboard.id }, {
          onSuccess: (item) => { toast.success(`Duplicated "${item.name}".`); onSelect(item.id); },
          onError: () => toast.error("Couldn't duplicate the dashboard.", { id: MUTATION_ERROR_TOAST_ID }),
        });
        break;
    }
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Switch dashboard — currently ${label}`}
        className="focus-ring pressable"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, height: 32, boxSizing: 'border-box',
          padding: '0 12px', background: open ? 'var(--surface-inset)' : 'var(--search-pill-bg)',
          border: '1px solid var(--search-pill-border)', borderRadius: 'var(--radius-pill)', cursor: 'pointer',
          transition: 'background 0.12s',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text)',
            whiteSpace: 'nowrap', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis',
          }}
        >
          {label}
        </span>
        <ChevronIcon open={open} />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Switch dashboard"
          className="pop-in"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: 0, minWidth: 260, maxHeight: 400,
            overflowY: 'auto', background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-panel)', boxShadow: 'var(--shadow-2)', zIndex: 30, padding: 4,
            ['--pop-origin' as unknown as string]: 'top left',
          }}
        >
          {personal.length > 0 && (
            <>
              <div style={{ padding: '6px 10px 2px', fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>
                Personal
              </div>
              {personal.map((d) => (
                <SwitcherItem
                  key={d.id}
                  dashboard={d}
                  active={d.id === activeDashboardId}
                  memberId={memberId}
                  canManageShared={canManageShared}
                  onSelect={() => choose(d.id)}
                  onLifecycleAction={handleLifecycleAction}
                />
              ))}
            </>
          )}
          {shared.length > 0 && (
            <>
              <div style={{ padding: '6px 10px 2px', fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>
                Shared
              </div>
              {shared.map((d) => (
                <SwitcherItem
                  key={d.id}
                  dashboard={d}
                  active={d.id === activeDashboardId}
                  memberId={memberId}
                  canManageShared={canManageShared}
                  onSelect={() => choose(d.id)}
                  onLifecycleAction={handleLifecycleAction}
                />
              ))}
            </>
          )}
          <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
          <button
            type="button"
            onClick={() => { setOpen(false); setShowNew(true); }}
            className="focus-ring"
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px',
              borderRadius: 'var(--radius-sm)', background: 'transparent', border: 'none', cursor: 'pointer',
              textAlign: 'left', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 500,
              color: 'var(--brand)',
            }}
          >
            + New dashboard
          </button>
        </div>
      )}

      {showNew && (
        <NewDashboardModal
          context={context}
          accountId={accountId}
          activeDashboardId={activeDashboardId}
          canManageShared={canManageShared}
          onClose={() => setShowNew(false)}
          onCreated={(id) => { setShowNew(false); onSelect(id); }}
        />
      )}

      {lifecycleModal?.kind === 'rename' && (
        <RenameDashboardModal dashboard={lifecycleModal.dashboard} onClose={() => setLifecycleModal(null)} />
      )}
      {lifecycleModal?.kind === 'publish' && (
        <PublishSharedCopyModal
          dashboard={lifecycleModal.dashboard}
          onClose={() => setLifecycleModal(null)}
          onPublished={(id) => { setLifecycleModal(null); onSelect(id); }}
        />
      )}
      {lifecycleModal?.kind === 'delete' && (
        <DeleteDashboardModal
          dashboard={lifecycleModal.dashboard}
          isActive={lifecycleModal.dashboard.id === activeDashboardId}
          onActiveDeleted={onActiveDeleted}
          onClose={() => setLifecycleModal(null)}
        />
      )}
    </div>
  );
}

function NewDashboardModal({
  context, accountId, activeDashboardId, canManageShared, onClose, onCreated,
}: {
  context: DashboardContext;
  accountId: string | null;
  activeDashboardId: string | null;
  canManageShared: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState('');
  const [mode, setMode] = useState<'blank' | 'duplicate'>('blank');
  const [visibility, setVisibility] = useState<'PERSONAL' | 'SHARED'>('PERSONAL');
  const [error, setError] = useState<string | null>(null);
  const create = useCreateDashboard();
  const duplicate = useDuplicateDashboard();

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (mode === 'blank' && trimmed.length === 0) {
      setError('Give this dashboard a name.');
      return;
    }
    setError(null);
    if (mode === 'duplicate' && activeDashboardId) {
      duplicate.mutate({ id: activeDashboardId }, {
        onSuccess: (item) => { toast.success(`Duplicated "${item.name}".`); onCreated(item.id); },
        onError: () => toast.error("Couldn't duplicate the dashboard.", { id: MUTATION_ERROR_TOAST_ID }),
      });
      return;
    }
    create.mutate(
      { name: trimmed, visibility, context, accountId: context === 'ACCOUNT' ? accountId : undefined },
      {
        onSuccess: (item) => { toast.success(`Created "${item.name}".`); onCreated(item.id); },
        onError: () => toast.error("Couldn't create the dashboard.", { id: MUTATION_ERROR_TOAST_ID }),
      },
    );
  }

  const pending = create.isPending || duplicate.isPending;

  return (
    <FormModal title="New dashboard" onClose={onClose} width={400}>
      <form onSubmit={handleSubmit} noValidate>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <button
            type="button" onClick={() => setMode('blank')} className="pressable"
            style={{ ...(mode === 'blank' ? btnPrimaryStyle : btnGhostStyle), height: 32, fontSize: 'var(--text-sm)', flex: 1 }}
          >
            Blank
          </button>
          <button
            type="button" onClick={() => setMode('duplicate')} disabled={!activeDashboardId} className="pressable"
            style={{
              ...(mode === 'duplicate' ? btnPrimaryStyle : btnGhostStyle), height: 32, fontSize: 'var(--text-sm)', flex: 1,
              opacity: activeDashboardId ? 1 : 0.4,
            }}
          >
            Duplicate current
          </button>
        </div>
        {mode === 'blank' && (
          <>
            <div style={{ marginBottom: 14 }}>
              <label htmlFor="new-dash-name" style={labelStyle}>Name</label>
              <input id="new-dash-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} className="focus-ring" style={fieldStyle} />
            </div>
            {canManageShared && (
              <div style={{ marginBottom: 14 }}>
                <label htmlFor="new-dash-visibility" style={labelStyle}>Visibility</label>
                <select
                  id="new-dash-visibility"
                  value={visibility}
                  onChange={(e) => setVisibility(e.target.value as 'PERSONAL' | 'SHARED')}
                  className="focus-ring"
                  style={fieldStyle}
                >
                  <option value="PERSONAL">Personal — only you</option>
                  <option value="SHARED">Shared — everyone</option>
                </select>
              </div>
            )}
          </>
        )}
        {error && <span style={errorTextStyle}>{error}</span>}
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} className="pressable focus-ring" style={btnGhostStyle}>Cancel</button>
          <button type="submit" disabled={pending} className="pressable focus-ring" style={{ ...btnPrimaryStyle, opacity: pending ? 0.6 : 1 }}>
            {pending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </FormModal>
  );
}

function RenameDashboardModal({ dashboard, onClose }: { dashboard: DashboardResponse; onClose: () => void }) {
  const [name, setName] = useState(dashboard.name);
  const [error, setError] = useState<string | null>(null);
  const update = useUpdateDashboard();

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setError('Give this dashboard a name.');
      return;
    }
    setError(null);
    // Echo accountId so a rename never relocates the dashboard's scope.
    update.mutate(
      { id: dashboard.id, payload: { name: trimmed, accountId: dashboard.accountId } },
      {
        onSuccess: (item) => { toast.success(`Renamed to "${item.name}".`); onClose(); },
        onError: () => toast.error("Couldn't rename the dashboard.", { id: MUTATION_ERROR_TOAST_ID }),
      },
    );
  }

  return (
    <FormModal title="Rename dashboard" onClose={onClose} width={400}>
      <form onSubmit={handleSubmit} noValidate>
        <div style={{ marginBottom: 14 }}>
          <label htmlFor="rename-dash-name" style={labelStyle}>Name</label>
          <input id="rename-dash-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} className="focus-ring" style={fieldStyle} />
        </div>
        {error && <span style={errorTextStyle}>{error}</span>}
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} className="pressable focus-ring" style={btnGhostStyle}>Cancel</button>
          <button type="submit" disabled={update.isPending} className="pressable focus-ring" style={{ ...btnPrimaryStyle, opacity: update.isPending ? 0.6 : 1 }}>
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </FormModal>
  );
}

function PublishSharedCopyModal({ dashboard, onClose, onPublished }: {
  dashboard: DashboardResponse;
  onClose: () => void;
  onPublished: (id: string) => void;
}) {
  const duplicate = useDuplicateDashboard();

  function handlePublish() {
    duplicate.mutate(
      { id: dashboard.id, visibility: 'SHARED' },
      {
        onSuccess: (item) => { toast.success(`Published "${item.name}" to everyone.`); onPublished(item.id); },
        onError: () => toast.error("Couldn't publish a shared copy.", { id: MUTATION_ERROR_TOAST_ID }),
      },
    );
  }

  return (
    <FormModal title="Publish a shared copy" onClose={onClose} width={400}>
      <p style={{ margin: '0 0 16px', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.5 }}>
        {"Creates a shared copy everyone can see. Widgets built on your private metrics will show 'not visible' to others — share those metrics from the catalog first."}
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onClose} className="pressable focus-ring" style={btnGhostStyle}>Cancel</button>
        <button
          type="button" onClick={handlePublish} disabled={duplicate.isPending}
          className="pressable focus-ring" style={{ ...btnPrimaryStyle, opacity: duplicate.isPending ? 0.6 : 1 }}
        >
          {duplicate.isPending ? 'Publishing…' : 'Publish shared copy'}
        </button>
      </div>
    </FormModal>
  );
}

function DeleteDashboardModal({ dashboard, isActive, onActiveDeleted, onClose }: {
  dashboard: DashboardResponse;
  isActive: boolean;
  onActiveDeleted?: () => void;
  onClose: () => void;
}) {
  // Enabled while open (the modal only mounts when a delete is staged) — reads
  // the live widget count so the confirm names the true blast radius.
  const { data: detail } = useDashboard(dashboard.id);
  const del = useDeleteDashboard();
  const widgetCount = detail?.widgets?.length;
  const countLabel = widgetCount === undefined ? '…' : String(widgetCount);

  function handleDelete() {
    del.mutate(dashboard.id, {
      onSuccess: () => {
        toast.success(`Deleted "${dashboard.name}".`);
        // Deleting the dashboard the user is looking at: hand navigation back
        // to the page so it can drop the dead ?dashboardId and show the default.
        if (isActive) onActiveDeleted?.();
        onClose();
      },
      onError: () => toast.error("Couldn't delete the dashboard.", { id: MUTATION_ERROR_TOAST_ID }),
    });
  }

  return (
    <FormModal title="Delete dashboard" onClose={onClose} width={400}>
      <p style={{ margin: '0 0 16px', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--text)', lineHeight: 1.5 }}>
        {`Delete "${dashboard.name}" and its ${countLabel} widget(s)? This can't be undone.`}
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onClose} className="pressable focus-ring" style={btnGhostStyle}>Cancel</button>
        <button
          type="button" onClick={handleDelete} disabled={del.isPending}
          className="pressable focus-ring" style={{ ...btnDangerStyle, opacity: del.isPending ? 0.6 : 1 }}
        >
          {del.isPending ? 'Deleting…' : 'Delete permanently'}
        </button>
      </div>
    </FormModal>
  );
}
